import { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  createTemplate,
  updateTemplate,
} from "../../../services/emailTemplateService";
import RichTextEditor from "./RichTextEditor";
import TagInput from "./TagInput";

const extractEmailContent = (html) => {
  if (!html) return "";

  try {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const contentDiv = tempDiv.querySelector(".email-content");
    return contentDiv ? contentDiv.innerHTML.trim() : "";
  } catch (error) {
    console.error("Error extracting email content:", error);
    return "";
  }
};

const EmailCreateTemplateModal = ({ onClose, existingData }) => {
  const modalRef = useRef(null);

  // Only set email-content as initial description
  const [description, setDescription] = useState(() => {
    if (existingData?.originalContent) {
      return existingData.originalContent;
    }
    if (existingData?.sampleMessage) {
      return extractEmailContent(existingData.sampleMessage);
    }
    return "";
  });

  const [finalPreview, setFinalPreview] = useState(
    existingData?.sampleMessage || ""
  );
  const [templateName, setTemplateName] = useState(
    existingData?.templateName || ""
  );
  const [tags, setTags] = useState(existingData?.placeholders || []);
  const [subject, setSubject] = useState(existingData?.subject || "");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!templateName.trim() || !subject.trim()) {
      toast.error("All fields are required");
      return;
    }

    const payload = {
      templateName: templateName.trim(),
      sampleMessage: finalPreview,
      originalContent: description,
      placeholders: tags,
      subject: subject,
    };

    const action = existingData
      ? updateTemplate(existingData._id, payload)
      : createTemplate(payload);

    toast
      .promise(action, {
        loading: existingData ? "Updating template..." : "Creating template...",
        success: existingData
          ? "Template updated successfully!"
          : "Template created successfully!",
        error: (err) => err.message || "Something went wrong",
      })
      .then(() => {
        onClose();
        window.location.reload();
      });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white relative w-full animate-scale-in max-w-4xl max-h-[90vh] overflow-auto purple-scroll rounded-[20px] shadow-lg"
      >
        <h2 className="text-center text-blue font-semibold text-sm mb-4 mt-8">
          {existingData ? "Update Template" : "Create Template"}
        </h2>

        <div
          onClick={onClose}
          className="absolute right-5 top-3 cursor-pointer text-black font-bold"
        >
          ✕
        </div>

        <div className="space-y-4 p-4">
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Template Name
            </label>
            <input
              type="text"
              className="w-full border border-[#0000003D] bg-[#FAFAFA] rounded-[10px] px-3 h-[48px] text-sm"
              placeholder="e.g. Order Confirmation"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Subject
              </label>
              <input
                type="text"
                className="w-full border border-[#0000003D] bg-[#FAFAFA] rounded-[10px] px-3 h-[48px] text-sm"
                placeholder="e.g. Order Confirmation"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <TagInput tags={tags} setTags={setTags} />
          </div>

          <RichTextEditor
            description={description}
            onChange={setDescription}
            setFinalPreview={setFinalPreview}
          />

          <div
            onClick={handleSubmit}
            className="w-full bg-[#4F22D8] flex justify-center items-center text-white rounded-[10px] py-[10px] text-sm cursor-pointer"
          >
            {existingData ? "Update Template" : "Create Template"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCreateTemplateModal;
